// ============================================================================
//  Frontend Trésorerie — NexusCorp Phase 2
//  TreasuryDashboard · BankAccountsPage · TransactionsPage
//  ReconciliationPage · ForecastPage
// ============================================================================

import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Wallet, TrendingUp, TrendingDown, AlertTriangle, Plus, Search,
  CheckCircle, XCircle, RefreshCw, Download, ChevronLeft, ChevronRight,
  ArrowUpRight, ArrowDownLeft, Minus, X,
} from 'lucide-react'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts'
import { treasuryApi, type BankAccount, type CashTransaction, type TreasuryForecast } from '@/modules/treasury/api/treasury.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore } from '@/stores/auth.store'

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Convertit Decimal.js Prisma { s, e, d } ou number/string en number JS
const toNum = (v: any): number => {
  if (v === null || v === undefined) return 0
  if (typeof v === 'number') return v
  if (typeof v === 'string') return parseFloat(v) || 0
  if (typeof v === 'object' && Array.isArray(v.d) && typeof v.e === 'number' && v.d.length > 0) {
    const sign = v.s < 0 ? -1 : 1
    const d0   = v.d[0]
    const exp  = v.e - String(d0).length + 1
    return sign * d0 * Math.pow(10, exp)
  }
  return Number(v) || 0
}

const fmt = (n: any) =>
  new Intl.NumberFormat('fr-SN', { minimumFractionDigits: 0 }).format(Math.round(toNum(n)))

const TX_TYPE_CONFIG = {
  RECEIPT:      { label: 'Encaissement',  icon: ArrowDownLeft,  color: 'text-[#059669]', bg: 'bg-[#10B981]/10' },
  PAYMENT:      { label: 'Décaissement',  icon: ArrowUpRight,   color: 'text-[#DC2626]',     bg: 'bg-[#FEF2F2]' },
  TRANSFER_IN:  { label: 'Virement entrant',  icon: ArrowDownLeft, color: 'text-[var(--blue-d)]', bg: 'bg-[#E0F2FE]' },
  TRANSFER_OUT: { label: 'Virement sortant',  icon: ArrowUpRight,  color: 'text-[#D97706]', bg: 'bg-[#FEF3C7]' },
  FEE:          { label: 'Frais bancaires', icon: Minus,         color: 'text-[var(--tx3)]',  bg: 'bg-[#D6D2EC]/10' },
  INTEREST:     { label: 'Intérêts',       icon: TrendingUp,    color: 'text-[#0E7490]',   bg: 'bg-[#ECFEFF]' },
} as const

const STATUS_CONFIG = {
  PENDING:    { label: 'À rapprocher', color: 'text-[#D97706]',   bg: 'bg-[#FEF3C7]  border-[rgba(245,158,11,0.25)]' },
  RECONCILED: { label: 'Rapproché',    color: 'text-[#059669]', bg: 'bg-[#10B981]/10 border-[rgba(16,185,129,0.25)]' },
  CANCELLED:  { label: 'Annulé',       color: 'text-[var(--tx3)]',   bg: 'bg-[#D6D2EC]/10   border-[var(--tx3)]/20' },
} as const

// ─── Modal Compte Bancaire ────────────────────────────────────────────────────

function CreateBankAccountModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const [form, setForm] = useState({
    name: '', bankName: '', accountNumber: '', iban: '', swift: '',
    currency: 'XOF', openingBalance: '', lowBalanceAlert: '', accountingCode: '',
  })

  const mutation = useMutation({
    mutationFn: () => treasuryApi.createBankAccount({
      ...form,
      currency:       form.currency || 'XOF',
      openingBalance: form.openingBalance  ? parseFloat(form.openingBalance)  : 0,
      lowBalanceAlert: form.lowBalanceAlert ? parseFloat(form.lowBalanceAlert) : undefined,
    }),
    onSuccess: (acc) => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] })
      addNotification({ type: 'success', title: 'Compte créé', message: `${acc.bankName} — ${acc.accountNumber}` })
      onClose()
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message })
    },
  })

  const F = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }))

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-[rgba(13,8,32,0.50)] backdrop-blur-sm">
      <div className="bg-white border border-[var(--bd)] rounded-xl w-full max-w-lg mx-4 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--brand-dark)]">Nouveau compte bancaire</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-[var(--tx3)] hover:text-[var(--brand-dark)]" /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          {([
            ['Nom du compte *',      'name',           'text', 'ex: Compte principal CBAO'],
            ['Banque *',             'bankName',       'text', 'ex: CBAO, Ecobank, BOA'],
            ['Numéro de compte *',   'accountNumber',  'text', 'ex: 00038-01234567890-12'],
            ['IBAN',                 'iban',           'text', 'SN…'],
            ['SWIFT/BIC',            'swift',          'text', 'ex: CBAOSNDA'],
            ['Solde d\'ouverture',   'openingBalance', 'number', '0'],
            ['Seuil d\'alerte (XOF)', 'lowBalanceAlert','number', 'Laisser vide = aucune alerte'],
            ['Compte SYSCOHADA',     'accountingCode', 'text', 'ex: 521'],
          ] as const).map(([label, key, type, ph]) => (
            <div key={key} className={key === 'name' || key === 'accountNumber' ? 'col-span-2' : ''}>
              <label className="block text-xs text-[var(--tx3)] mb-1">{label}</label>
              <input
                type={type}
                value={form[key as keyof typeof form]}
                onChange={e => F(key as keyof typeof form, e.target.value)}
                placeholder={ph}
                className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1">Devise</label>
            <select
              value={form.currency}
              onChange={e => F('currency', e.target.value)}
              className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
            >
              <option value="XOF">XOF — Franc CFA UEMOA (Sénégal)</option>
              <option value="EUR">EUR — Euro</option>
              <option value="USD">USD — Dollar US</option>
            </select>
          </div>
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-[var(--bd)]">
          <button onClick={onClose} className="flex-1 py-2 text-sm text-[var(--tx3)] border border-[var(--bd)] rounded-lg">Annuler</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!form.name || !form.bankName || !form.accountNumber || mutation.isPending}
            className="flex-1 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {mutation.isPending ? 'Création…' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal Nouvelle Transaction ───────────────────────────────────────────────

function CreateTransactionModal({ open, onClose, accounts }: {
  open: boolean; onClose: () => void; accounts: BankAccount[]
}) {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const [form, setForm] = useState({
    bankAccountId: '', type: 'RECEIPT', amount: '',
    date: new Date().toISOString().slice(0, 10),
    description: '', reference: '', thirdPartyName: '',
  })

  const mutation = useMutation({
    mutationFn: () => treasuryApi.createTransaction({
      ...form,
      amount: parseFloat(form.amount),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] })
      qc.invalidateQueries({ queryKey: ['bank-accounts'] })
      addNotification({ type: 'success', title: 'Transaction créée', message: `${form.type} — ${fmt(parseFloat(form.amount))} XOF` })
      onClose()
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message })
    },
  })

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-[rgba(13,8,32,0.50)] backdrop-blur-sm">
      <div className="bg-white border border-[var(--bd)] rounded-xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--brand-dark)]">Nouvelle transaction</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-[var(--tx3)] hover:text-[var(--brand-dark)]" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1">Compte bancaire *</label>
            <select
              value={form.bankAccountId}
              onChange={e => setForm(f => ({ ...f, bankAccountId: e.target.value }))}
              className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
            >
              <option value="">Sélectionner…</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {a.bankName}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[var(--tx3)] mb-1">Type *</label>
              <select
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
              >
                {Object.entries(TX_TYPE_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[var(--tx3)] mb-1">Montant (XOF) *</label>
              <input
                type="number"
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="0"
                className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[var(--tx3)] mb-1">Date *</label>
              <input
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-[var(--tx3)] mb-1">Référence</label>
              <input
                value={form.reference}
                onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
                placeholder="Réf. chèque, virement…"
                className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1">Libellé *</label>
            <input
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Description de la transaction…"
              className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1">Tiers</label>
            <input
              value={form.thirdPartyName}
              onChange={e => setForm(f => ({ ...f, thirdPartyName: e.target.value }))}
              placeholder="Client, fournisseur…"
              className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-[var(--bd)]">
          <button onClick={onClose} className="flex-1 py-2 text-sm text-[var(--tx3)] border border-[var(--bd)] rounded-lg">Annuler</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!form.bankAccountId || !form.amount || !form.description || mutation.isPending}
            className="flex-1 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
//  TREASURY DASHBOARD
// ============================================================================

export function TreasuryDashboard() {
  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn:  () => treasuryApi.getBankAccounts(),
  })

  const { data: forecast } = useQuery({
    queryKey: ['forecast', 30],
    queryFn:  () => treasuryApi.getCashFlowForecast(30),
  })

  const { data: recentTx } = useQuery({
    queryKey: ['transactions', 'recent'],
    queryFn:  () => treasuryApi.getTransactions({ limit: 8 }),
  })

  const totalBalance = accounts.reduce((s, a) => s + toNum(a.currentBalance), 0)
  const alertAccounts = accounts.filter(a => a.lowBalanceAlert && toNum(a.currentBalance) < toNum(a.lowBalanceAlert))

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null
    return (
      <div className="bg-white border border-[var(--bd)] rounded-lg p-3 text-xs">
        <p className="text-[var(--tx3)] mb-1">{label}</p>
        <p className="text-[var(--blue-d)]">Solde: {fmt(payload[0]?.value)} XOF</p>
      </div>
    )
  }

  return (
    <div className="space-y-5 nx-fade-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Trésorerie</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Comptes bancaires · Rapprochement · Prévisions · XOF</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/treasury/transactions" className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white text-sm font-medium rounded-lg transition-colors">
            <Plus className="w-4 h-4" />
            Transaction
          </a>
        </div>
      </div>

      {/* Alertes solde bas */}
      {alertAccounts.map(acc => (
        <div key={acc.id} className="flex items-center gap-3 px-4 py-3 bg-[#FEF2F2] border border-[rgba(239,68,68,0.25)] rounded-xl text-[#DC2626] text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>
            <strong>{acc.name}</strong> ({acc.bankName}) — Solde {fmt(acc.currentBalance)} XOF
            sous le seuil d'alerte ({fmt(acc.lowBalanceAlert!)} XOF).
          </span>
        </div>
      ))}

      {/* KPI solde global */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 nx-stagger">
        <div className="col-span-2 bg-gradient-to-br from-blue-600/20 to-blue-800/10 border border-[rgba(245,124,0,0.25)] rounded-xl p-5">
          <p className="text-sm text-[var(--blue-d)] mb-2">Position de trésorerie globale</p>
          <p className="text-3xl font-bold text-[var(--brand-dark)] tabular-nums">{fmt(totalBalance)}</p>
          <p className="text-sm text-[var(--blue-d)] mt-1">XOF · {accounts.length} compte(s)</p>
        </div>
        {[
          { label: 'Transactions à rapprocher', value: recentTx?.transactions?.filter(t => t.status === 'PENDING').length ?? 0, color: 'text-[#D97706]', sub: 'Montant déjà débité — en attente de rapprochement comptable' },
          { label: 'Jours critiques', value: forecast?.criticalDays ?? 0, color: forecast?.criticalDays ? 'text-[#DC2626]' : 'text-[#059669]', sub: '30 prochains jours' },
        ].map(item => (
          <div key={item.label} className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
            <p className="text-sm text-[var(--tx3)] mb-2">{item.label}</p>
            <p className={`text-3xl font-bold ${item.color}`}>{item.value}</p>
            <p className="text-xs text-[var(--tx3)] mt-1">{item.sub}</p>
          </div>
        ))}
      </div>

      {/* Comptes bancaires */}
      <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[var(--brand-dark)]">Comptes bancaires</h3>
          <a href="/treasury/accounts" className="text-xs text-[var(--blue-d)] hover:text-blue-300">Gérer →</a>
        </div>
        {isLoading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-[var(--surface-2)]/30 rounded-lg animate-pulse" />)}</div>
        ) : (
          <div className="space-y-2">
            {accounts.map(acc => {
              const isAlert = acc.lowBalanceAlert && toNum(acc.currentBalance) < toNum(acc.lowBalanceAlert)
              return (
                <div key={acc.id} className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${isAlert ? 'bg-red-500/5 border-[rgba(239,68,68,0.25)]' : 'bg-[var(--surface-2)]/20 border-[var(--bd)] hover:bg-[#F4F2FB]'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[var(--surface-2)] rounded-lg flex items-center justify-center">
                      <Wallet className={`w-4 h-4 ${isAlert ? 'text-[#DC2626]' : 'text-[var(--blue-d)]'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[var(--brand-dark)]">{acc.name}</p>
                      <p className="text-xs text-[var(--tx3)]">{acc.bankName} · {acc.accountNumber}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-bold tabular-nums ${isAlert ? 'text-[#DC2626]' : 'text-[var(--brand-dark)]'}`}>
                      {fmt(acc.currentBalance)} <span className="text-xs font-normal text-[var(--tx3)]">{acc.currency}</span>
                    </p>
                    {acc._count && <p className="text-xs text-[var(--tx3)]">{acc._count.transactions} transactions</p>}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Prévision 30 jours */}
      {forecast && (
        <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[var(--brand-dark)]">Prévision trésorerie — 30 jours</h3>
            <a href="/treasury/forecast" className="text-xs text-[var(--blue-d)] hover:text-blue-300">Détail →</a>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={forecast.dailyFlow.slice(0, 30)} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="tg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
              <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={v => `${(v/1000000).toFixed(0)}M`} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="3 3" />
              <Area type="monotone" dataKey="cumulativeBalance" stroke="#3b82f6" fill="url(#tg)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
          {forecast.criticalDays > 0 && (
            <div className="mt-3 flex items-center gap-2 text-xs text-[#DC2626]">
              <AlertTriangle className="w-3.5 h-3.5" />
              {forecast.criticalDays} jour(s) avec solde négatif prévu — réapprovisionnement recommandé.
            </div>
          )}
        </div>
      )}

      {/* Dernières transactions */}
      <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[var(--brand-dark)]">Dernières transactions</h3>
          <a href="/treasury/transactions" className="text-xs text-[var(--blue-d)] hover:text-blue-300">Toutes →</a>
        </div>
        <div className="space-y-1">
          {(recentTx?.transactions ?? []).slice(0, 6).map(tx => {
            const cfg  = TX_TYPE_CONFIG[tx.type] ?? TX_TYPE_CONFIG.RECEIPT
            const sCfg = STATUS_CONFIG[tx.status]
            const Icon = cfg.icon
            return (
              <div key={tx.id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[#F4F2FB] transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${cfg.bg}`}>
                    <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[var(--brand-dark)] truncate max-w-[180px]">{tx.description}</p>
                    <p className="text-xs text-[var(--tx3)]">{tx.date.slice(0, 10)} · {tx.bankAccount?.bankName}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-xs font-bold tabular-nums ${cfg.color}`}>
                    {['RECEIPT','TRANSFER_IN','INTEREST'].includes(tx.type) ? '+' : '−'}{fmt(tx.amount)} XOF
                  </p>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${sCfg?.bg ?? ''} ${sCfg?.color ?? ''}`}>
                    {sCfg?.label}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
//  BANK ACCOUNTS PAGE
// ============================================================================

export function BankAccountsPage() {
  const { hasPermission } = useAuthStore()
  const [modal, setModal] = useState(false)

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['bank-accounts', true],
    queryFn:  () => treasuryApi.getBankAccounts(true),
  })

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Comptes Bancaires</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">{accounts.length} compte(s) · XOF</p>
        </div>
        {hasPermission('treasury:manage') && (
          <button
            onClick={() => setModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouveau compte
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-32 bg-[var(--surface-2)] rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accounts.map(acc => {
            const isAlert = acc.lowBalanceAlert && toNum(acc.currentBalance) < toNum(acc.lowBalanceAlert)
            return (
              <div key={acc.id} className={`bg-[var(--surface-2)] border rounded-xl p-5 ${isAlert ? 'border-[rgba(239,68,68,0.35)]' : 'border-[var(--bd)]'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-[var(--brand-dark)]">{acc.name}</p>
                      {!acc.isActive && <span className="text-xs px-1.5 py-0.5 bg-[var(--surface-2)] text-[var(--tx3)] rounded">Inactif</span>}
                    </div>
                    <p className="text-xs text-[var(--tx3)] mt-0.5">{acc.bankName}</p>
                    <p className="text-xs text-[var(--tx3)] font-mono mt-1">{acc.accountNumber}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${isAlert ? 'bg-[#FEF2F2] border-[rgba(239,68,68,0.25)] text-[#DC2626]' : 'bg-[#10B981]/10 border-[rgba(16,185,129,0.25)] text-[#059669]'}`}>
                    {acc.currency}
                  </span>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xs text-[var(--tx3)] mb-0.5">Solde courant</p>
                    <p className={`text-xl font-bold tabular-nums ${isAlert ? 'text-[#DC2626]' : 'text-[var(--brand-dark)]'}`}>
                      {fmt(acc.currentBalance)} <span className="text-xs font-normal text-[var(--tx3)]">XOF</span>
                    </p>
                    {isAlert && (
                      <p className="text-xs text-[#DC2626] mt-1">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        Sous seuil ({fmt(acc.lowBalanceAlert!)} XOF)
                      </p>
                    )}
                  </div>
                  {acc.accountingCode && (
                    <span className="text-xs px-2 py-1 bg-[var(--surface-2)] rounded text-[var(--tx3)] font-mono">
                      {acc.accountingCode}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <CreateBankAccountModal open={modal} onClose={() => setModal(false)} />
    </div>
  )
}

// ============================================================================
//  TRANSACTIONS PAGE
// ============================================================================

export function TransactionsPage() {
  const { hasPermission } = useAuthStore()
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()

  const [modal, setModal]         = useState(false)
  const [accountId, setAccountId] = useState('')
  const [status, setStatus]       = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate]     = useState('')
  const [page, setPage]           = useState(1)

  const { data: accounts = [] } = useQuery({ queryKey: ['bank-accounts'], queryFn: () => treasuryApi.getBankAccounts() })

  const { data: result, isLoading } = useQuery({
    queryKey: ['transactions', accountId, status, startDate, endDate, page],
    queryFn:  () => treasuryApi.getTransactions({
      bankAccountId: accountId   || undefined,
      status:        status      || undefined,
      startDate:     startDate   || undefined,
      endDate:       endDate     || undefined,
      page, limit: 30,
    }),
  })

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => treasuryApi.cancelTransaction(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] })
      qc.invalidateQueries({ queryKey: ['bank-accounts'] })
      addNotification({ type: 'success', title: 'Transaction annulée', message: '' })
    },
  })

  const exportCsv = () => {
    const txs = result?.transactions ?? []
    if (!txs.length) return
    const SEP = ';'
    const NL  = String.fromCharCode(10)
    const BOM = String.fromCharCode(0xFEFF)
    const headers = ['Date','Libelle','Tiers','Compte','Type','Montant','Statut','Reference'].join(SEP)
    const rows = txs.map((tx: any) =>
      [(tx.date ?? '').slice(0,10), (tx.description ?? ''), (tx.thirdPartyName ?? ''), tx.bankAccount?.bankName ?? '', tx.type ?? '', Number(tx.amount ?? 0), tx.status ?? '', tx.reference ?? ''].join(SEP)
    ).join(NL)
    const blob = new Blob([BOM + headers + NL + rows], { type: 'text/csv;charset=utf-8' })
    const url  = URL.createObjectURL(blob)
    const a    = Object.assign(document.createElement('a'), { href: url, download: 'transactions-' + new Date().toISOString().slice(0, 10) + '.csv' })
    a.click()
    URL.revokeObjectURL(url)
  }

    return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Transactions</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">{result?.total ?? 0} transaction(s)</p>
        </div>
        {hasPermission('treasury:create') && (
          <button
            onClick={() => setModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvelle transaction
          </button>
        )}
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={accountId}
          onChange={e => { setAccountId(e.target.value); setPage(1) }}
          className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
        >
          <option value="">Tous les comptes</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {a.bankName}</option>)}
        </select>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1) }}
          className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none">
          <option value="">Tous les statuts</option>
          <option value="PENDING">À rapprocher</option>
          <option value="RECONCILED">Rapproché</option>
          <option value="CANCELLED">Annulé</option>
        </select>
        <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1) }}
          className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none" title="Date début" />
        <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1) }}
          className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none" title="Date fin" />
        <button onClick={exportCsv} disabled={!result?.transactions?.length}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-[var(--bd)] text-[var(--tx2)] text-sm rounded-lg hover:bg-[var(--surface-2)] disabled:opacity-40 transition-colors">
          <Download className="w-4 h-4" />Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
          <tr className="border-b border-[var(--bd)] text-[var(--tx3)]">
            <th className="text-left px-3 py-2.5 w-24">Date</th>
            <th className="text-left px-3 py-2.5">Libellé</th>
            <th className="text-left px-3 py-2.5 w-32">Compte</th>
            <th className="text-left px-3 py-2.5 w-28">Type</th>
            <th className="text-right px-3 py-2.5 w-28">Montant</th>
            <th className="text-left px-3 py-2.5 w-24">Statut</th>
            <th className="w-8" />
          </tr>
          </thead>
          <tbody className="divide-y divide-[var(--bd)]">
          {isLoading
            ? [...Array(6)].map((_, i) => (
              <tr key={i}><td colSpan={7} className="px-3 py-3"><div className="h-4 bg-[#F4F2FB] rounded animate-pulse" /></td></tr>
            ))
            : (result?.transactions ?? []).map(tx => {
              const cfg   = TX_TYPE_CONFIG[tx.type] ?? TX_TYPE_CONFIG.RECEIPT
              const sCfg  = STATUS_CONFIG[tx.status]
              const Icon  = cfg.icon
              const isPos = ['RECEIPT','TRANSFER_IN','INTEREST'].includes(tx.type)
              return (
                <tr key={tx.id} className="hover:bg-[var(--surface-2)]/20 group">
                  <td className="px-3 py-2.5 text-[var(--tx3)]">{tx.date.slice(0,10)}</td>
                  <td className="px-3 py-2.5">
                    <p className="text-[var(--tx2)] truncate max-w-[220px]">{tx.description}</p>
                    {tx.thirdPartyName && <p className="text-[var(--tx3)] truncate max-w-[220px]">{tx.thirdPartyName}</p>}
                  </td>
                  <td className="px-3 py-2.5 text-[var(--tx3)] truncate">{tx.bankAccount?.bankName}</td>
                  <td className="px-3 py-2.5">
                        <span className={`flex items-center gap-1 ${cfg.color}`}>
                          <Icon className="w-3 h-3" />
                          {cfg.label}
                        </span>
                  </td>
                  <td className={`px-3 py-2.5 text-right font-bold tabular-nums ${cfg.color}`}>
                    {isPos ? '+' : '−'}{fmt(tx.amount)} XOF
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${sCfg?.bg}`}>{sCfg?.label}</span>
                  </td>
                  <td className="px-2 py-2.5">
                    {tx.status === 'PENDING' && hasPermission('treasury:manage') && (
                      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-all">
                        <button
                          onClick={() => reconcileMutation.mutate(tx.id)}
                          className="p-1 text-[#059669] hover:text-green-700 transition-colors"
                          title="Valider / Réconcilier"
                        >
                          <CheckCircle className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => cancelMutation.mutate({ id: tx.id, reason: 'Annulation manuelle' })}
                          className="p-1 text-[var(--tx3)] hover:text-[#DC2626] transition-colors"
                          title="Annuler"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })
          }
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(result?.pages ?? 0) > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[var(--tx3)]">{result?.total} transactions · Page {page}/{result?.pages}</p>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="p-2 border border-[var(--bd)] rounded-lg text-[var(--tx3)] hover:text-[var(--brand-dark)] disabled:opacity-40 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm text-[var(--tx3)]">{page}</span>
            <button onClick={() => setPage(p => p+1)} disabled={page >= (result?.pages ?? 1)} className="p-2 border border-[var(--bd)] rounded-lg text-[var(--tx3)] hover:text-[var(--brand-dark)] disabled:opacity-40 transition-colors"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      <CreateTransactionModal open={modal} onClose={() => setModal(false)} accounts={accounts} />
    </div>
  )
}

// ============================================================================
//  RECONCILIATION PAGE
// ============================================================================

export function ReconciliationPage() {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const [accountId, setAccountId] = useState('')

  const { data: accounts = [] } = useQuery({ queryKey: ['bank-accounts'], queryFn: () => treasuryApi.getBankAccounts() })

  const { data: pending } = useQuery({
    queryKey: ['transactions', accountId, 'PENDING'],
    queryFn:  () => treasuryApi.getTransactions({ bankAccountId: accountId || undefined, status: 'PENDING', limit: 50 }),
  })

  const autoMutation = useMutation({
    mutationFn: () => treasuryApi.autoReconcile(accountId, 1),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['transactions'] })
      addNotification({
        type: 'success',
        title: 'Rapprochement automatique',
        message: `${r.matched} transaction(s) rapprochée(s), ${r.unmatched} en attente.`,
      })
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message })
    },
  })

  const reconcileMutation = useMutation({
    mutationFn: (id: string) => treasuryApi.reconcileTransaction(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] })
      addNotification({ type: 'success', title: 'Transaction rapprochée', message: '' })
    },
  })

  const [showImport, setShowImport] = useState(false)
  const [importForm, setImportForm] = useState({ bankAccountId: '', statementDate: '', startDate: '', endDate: '', openingBalance: '', closingBalance: '' })

  const importMutation = useMutation({
    mutationFn: () => treasuryApi.importStatement({
      bankAccountId:  importForm.bankAccountId,
      statementDate:  importForm.statementDate,
      startDate:      importForm.startDate,
      endDate:        importForm.endDate,
      openingBalance: parseFloat(importForm.openingBalance) || 0,
      closingBalance: parseFloat(importForm.closingBalance) || 0,
      lines: [],
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] })
      setShowImport(false)
      setImportForm({ bankAccountId: '', statementDate: '', startDate: '', endDate: '', openingBalance: '', closingBalance: '' })
      addNotification({ type: 'success', title: 'Relevé importé', message: 'Rapprochement disponible.' })
    },
    onError: (e: any) => addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message }),
  })

  const pendingCount = pending?.total ?? 0

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Rapprochement Bancaire</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">
            {pendingCount} transaction(s) en attente
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={accountId}
            onChange={e => setAccountId(e.target.value)}
            className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
          >
            <option value="">Tous les comptes</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          {accountId && (
            <>
            <button onClick={() => setShowImport(true)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-[var(--bd)] text-[var(--tx2)] text-sm rounded-lg hover:bg-[var(--surface-2)] transition-colors">
              <Download className="w-4 h-4 rotate-180" />Importer relevé
            </button>
            <button
              onClick={() => autoMutation.mutate()}
              disabled={autoMutation.isPending || pendingCount === 0}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-[var(--brand-dark)] text-sm font-medium rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${autoMutation.isPending ? 'animate-spin' : ''}`} />
              Rapprochement auto
            </button>
            </>
          )}
        </div>
      </div>

      {pendingCount === 0 ? (
        <div className="text-center py-10">
          <CheckCircle className="w-10 h-10 text-[#059669] mx-auto mb-3" />
          <p className="text-[#059669] font-medium">Toutes les transactions sont rapprochées</p>
          <p className="text-[var(--tx3)] text-sm mt-1">Aucune transaction en attente</p>
        </div>
      ) : (
        <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
            <tr className="border-b border-[var(--bd)] text-[var(--tx3)]">
              <th className="text-left px-3 py-2.5">Date</th>
              <th className="text-left px-3 py-2.5">Libellé</th>
              <th className="text-left px-3 py-2.5">Type</th>
              <th className="text-right px-3 py-2.5">Montant</th>
              <th className="text-left px-3 py-2.5">Référence</th>
              <th className="w-28" />
            </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
            {(pending?.transactions ?? []).map(tx => {
              const cfg  = TX_TYPE_CONFIG[tx.type] ?? TX_TYPE_CONFIG.RECEIPT
              const isPos = ['RECEIPT','TRANSFER_IN','INTEREST'].includes(tx.type)
              return (
                <tr key={tx.id} className="hover:bg-[var(--surface-2)]/20">
                  <td className="px-3 py-2.5 text-[var(--tx3)]">{tx.date.slice(0,10)}</td>
                  <td className="px-3 py-2.5 text-[var(--tx2)] truncate max-w-[200px]">{tx.description}</td>
                  <td className={`px-3 py-2.5 ${cfg.color}`}>{cfg.label}</td>
                  <td className={`px-3 py-2.5 text-right font-bold tabular-nums ${cfg.color}`}>
                    {isPos ? '+' : '−'}{fmt(tx.amount)} XOF
                  </td>
                  <td className="px-3 py-2.5 text-[var(--tx3)] font-mono">{tx.reference ?? '—'}</td>
                  <td className="px-3 py-2.5">
                    <button
                      onClick={() => reconcileMutation.mutate(tx.id)}
                      disabled={reconcileMutation.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-[#059669] rounded-lg transition-colors text-xs"
                    >
                      <CheckCircle className="w-3 h-3" />
                      Rapprocher
                    </button>
                  </td>
                </tr>
              )
            })}
            </tbody>
          </table>
        </div>
      )}
      {showImport && (
        <div className="fixed inset-0 bg-[rgba(22,12,38,0.5)] backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white border border-[var(--bd)] rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[var(--tx)]">Importer un relevé bancaire</h3>
              <button onClick={() => setShowImport(false)} className="text-[var(--tx3)]">✕</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Compte *</label>
                <select value={importForm.bankAccountId} onChange={e => setImportForm(f => ({ ...f, bankAccountId: e.target.value }))}
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none">
                  <option value="">— Sélectionner —</option>
                  {(accounts as any[]).map((a: any) => <option key={a.id} value={a.id}>{a.name} — {a.bankName}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[['statementDate','Date relevé'],['startDate','Période début'],['endDate','Période fin']].map(([key, lbl]) => (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">{lbl} *</label>
                    <input type="date" value={(importForm as any)[key]}
                      onChange={e => setImportForm(f => ({ ...f, [key]: e.target.value }))}
                      className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[['openingBalance','Solde ouverture (XOF)'],['closingBalance','Solde clôture (XOF)']].map(([key, lbl]) => (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">{lbl} *</label>
                    <input type="number" value={(importForm as any)[key]}
                      onChange={e => setImportForm(f => ({ ...f, [key]: e.target.value }))}
                      placeholder="0"
                      className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowImport(false)} className="flex-1 py-2 bg-[var(--surface-2)] text-[var(--tx2)] rounded-lg text-sm">Annuler</button>
              <button onClick={() => importMutation.mutate()}
                disabled={!importForm.bankAccountId || !importForm.statementDate || importMutation.isPending}
                className="flex-1 py-2 bg-[var(--blue)] text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {importMutation.isPending ? 'Import…' : 'Importer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  FORECAST PAGE
// ============================================================================

export function ForecastPage() {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const { hasPermission } = useAuthStore()
  const [days, setDays]           = useState(30)
  const [accountId, setAccountId] = useState('')
  const [modal, setModal]         = useState(false)
  const [newFC, setNewFC]         = useState({ bankAccountId: '', type: 'RECEIPT', amount: '', expectedDate: '', description: '', probability: '100' })

  const { data: accounts = [] } = useQuery({ queryKey: ['bank-accounts'], queryFn: () => treasuryApi.getBankAccounts() })

  const { data: forecast, isLoading } = useQuery({
    queryKey: ['forecast', days, accountId],
    queryFn:  () => treasuryApi.getCashFlowForecast(days, accountId || undefined),
  })

  const { data: items = [] } = useQuery({
    queryKey: ['forecast-items', accountId],
    queryFn:  () => treasuryApi.getForecasts({ bankAccountId: accountId || undefined }),
  })

  const [actualizeId, setActualizeId] = useState<string | null>(null)
  const [actualAmount, setActualAmount] = useState('')
  const actualizeMutation = useMutation({
    mutationFn: ({ id, amount }: { id: string; amount: number }) =>
      treasuryApi.actualizeForecast(id, amount, new Date().toISOString().slice(0, 10)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['forecast-items'] })
      qc.invalidateQueries({ queryKey: ['forecast'] })
      setActualizeId(null); setActualAmount('')
      addNotification({ type: 'success', title: 'Prévision actualisée' })
    },
    onError: (e: any) => addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message }),
  })

    const createMutation = useMutation({
    mutationFn: () => treasuryApi.createForecast({
      bankAccountId: newFC.bankAccountId,
      type:          newFC.type,
      amount:        parseFloat(newFC.amount),
      expectedDate:  newFC.expectedDate,
      description:   newFC.description,
      probability:   parseInt(newFC.probability),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['forecast'] })
      qc.invalidateQueries({ queryKey: ['forecast-items'] })
      addNotification({ type: 'success', title: 'Prévision créée', message: '' })
      setModal(false)
    },
    onError: (e: any) => addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message }),
  })

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null
    return (
      <div className="bg-white border border-[var(--bd)] rounded-lg p-3 text-xs space-y-1">
        <p className="text-[var(--tx3)]">{label}</p>
        <p className="text-[#059669]">Entrées: +{fmt(payload.find((p: any) => p.dataKey === 'receipts')?.value ?? 0)}</p>
        <p className="text-[#DC2626]">Sorties: −{fmt(payload.find((p: any) => p.dataKey === 'payments')?.value ?? 0)}</p>
        <p className="text-[var(--blue-d)] font-bold">Solde: {fmt(payload.find((p: any) => p.dataKey === 'cumulativeBalance')?.value ?? 0)}</p>
      </div>
    )
  }

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Prévisions de Trésorerie</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Tableau de flux prévisionnel · XOF</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={accountId}
            onChange={e => setAccountId(e.target.value)}
            className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
          >
            <option value="">Tous les comptes</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          {[7, 30, 60, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${days === d ? 'bg-[var(--blue)] text-white' : 'bg-[var(--surface-2)] border border-[var(--bd)] text-[var(--tx3)] hover:text-[var(--tx)]'}`}
            >
              {d}j
            </button>
          ))}
          {hasPermission('treasury:manage') && (
            <button
              onClick={() => setModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Prévision
            </button>
          )}
        </div>
      </div>

      {/* KPIs */}
      {forecast && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Solde actuel',      value: forecast.currentBalance, color: 'text-[var(--brand-dark)]' },
            { label: 'Solde min prévu',   value: forecast.minBalance,     color: forecast.minBalance < 0 ? 'text-[#DC2626]' : 'text-[#059669]' },
            { label: 'Solde max prévu',   value: forecast.maxBalance,     color: 'text-[#059669]' },
            { label: 'Jours critiques',   value: forecast.criticalDays,   color: forecast.criticalDays > 0 ? 'text-[#DC2626]' : 'text-[#059669]', isCount: true },
          ].map(item => (
            <div key={item.label} className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
              <p className="text-xs text-[var(--tx3)] mb-1">{item.label}</p>
              <p className={`text-lg font-bold tabular-nums ${item.color}`}>
                {item.isCount ? item.value : `${fmt(item.value)} XOF`}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Graphique */}
      {isLoading ? (
        <div className="h-48 bg-[var(--surface-2)] rounded-xl animate-pulse" />
      ) : forecast && (
        <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)] mb-4">Évolution prévue — {days} jours</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={forecast.dailyFlow} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="fg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
              <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={v => `${(v/1000000).toFixed(1)}M`} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="4 4" label={{ value: 'Zéro', fill: '#ef4444', fontSize: 10 }} />
              <Area type="monotone" dataKey="cumulativeBalance" stroke="#3b82f6" fill="url(#fg)" strokeWidth={2} dot={false} name="cumulativeBalance" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Alerte jours critiques */}
      {(forecast?.alerts ?? []).length > 0 && (
        <div className="bg-[#FEF2F2] border border-[rgba(239,68,68,0.25)] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2 text-[#DC2626] text-sm font-medium">
            <AlertTriangle className="w-4 h-4" />
            Solde négatif prévu — {forecast!.alerts.length} jour(s)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {forecast!.alerts.map(a => (
              <div key={a.date} className="bg-[#FEF2F2] rounded-lg px-3 py-2 text-xs">
                <p className="text-red-300 font-medium">{a.date}</p>
                <p className="text-[#DC2626]">{fmt(a.balance)} XOF</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Liste prévisions */}
      <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)] mb-4">Prévisions saisies ({items.length})</h3>
        {items.length === 0 ? (
          <p className="text-xs text-[var(--tx3)] text-center py-6">Aucune prévision. Ajoutez des entrées/sorties attendues.</p>
        ) : (
          <div className="divide-y divide-[var(--bd)]">
            {items.map(fc => {
              const cfg   = TX_TYPE_CONFIG[fc.type as keyof typeof TX_TYPE_CONFIG] ?? TX_TYPE_CONFIG.RECEIPT
              const isPos = ['RECEIPT','TRANSFER_IN'].includes(fc.type)
              return (
                <div key={fc.id} className="flex items-center justify-between py-2.5 text-sm">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-mono ${fc.isActualized ? 'text-[var(--tx3)] line-through' : 'text-[var(--tx2)]'}`}>
                      {fc.expectedDate.slice(0,10)}
                    </span>
                    <span className={`text-xs ${cfg.color}`}>{cfg.label}</span>
                    <span className="text-[var(--tx2)] truncate max-w-[180px]">{fc.description}</span>
                    {fc.probability < 100 && (
                      <span className="text-xs text-[var(--tx3)]">{fc.probability}%</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-bold tabular-nums text-xs ${isPos ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
                      {isPos ? '+' : '−'}{fmt(fc.amount)} XOF
                    </span>
                    {!fc.isActualized && (
                      <button onClick={() => { setActualizeId(fc.id); setActualAmount(String(fc.amount)) }}
                        className="text-xs text-[var(--blue-d)] hover:underline">Actualiser</button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {actualizeId && (
        <div className="fixed inset-0 bg-[rgba(22,12,38,0.4)] backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white border border-[var(--bd)] rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-[var(--tx)] mb-3">Actualiser la prévision</h3>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Montant réel (XOF)</label>
              <input type="number" value={actualAmount} onChange={e => setActualAmount(e.target.value)}
                className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setActualizeId(null); setActualAmount('') }}
                className="flex-1 py-2 bg-[var(--surface-2)] text-[var(--tx2)] rounded-lg text-sm">Annuler</button>
              <button onClick={() => actualizeMutation.mutate({ id: actualizeId, amount: parseFloat(actualAmount) })}
                disabled={!actualAmount || actualizeMutation.isPending}
                className="flex-1 py-2 bg-[var(--green)] text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {actualizeMutation.isPending ? 'En cours…' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal nouvelle prévision */}
      {modal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-[rgba(13,8,32,0.50)] backdrop-blur-sm">
          <div className="bg-white border border-[var(--bd)] rounded-xl w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--bd)]">
              <h2 className="text-base font-semibold text-[var(--brand-dark)]">Nouvelle prévision</h2>
              <button onClick={() => setModal(false)}><X className="w-4 h-4 text-[var(--tx3)]" /></button>
            </div>
            <div className="p-5 space-y-4">
              {[
                ['Compte *', 'bankAccountId', 'select'],
                ['Type *',   'type',          'typeSelect'],
                ['Montant (XOF) *', 'amount', 'number'],
                ['Date prévue *', 'expectedDate', 'date'],
                ['Description *', 'description', 'text'],
                ['Probabilité (%)', 'probability', 'number'],
              ].map(([label, key, type]) => (
                <div key={key}>
                  <label className="block text-xs text-[var(--tx3)] mb-1">{label}</label>
                  {type === 'select' ? (
                    <select value={newFC[key as keyof typeof newFC]} onChange={e => setNewFC(f => ({ ...f, [key]: e.target.value }))}
                            className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500">
                      <option value="">Sélectionner…</option>
                      {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  ) : type === 'typeSelect' ? (
                    <select value={newFC[key as keyof typeof newFC]} onChange={e => setNewFC(f => ({ ...f, [key]: e.target.value }))}
                            className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500">
                      {Object.entries(TX_TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  ) : (
                    <input type={type} value={newFC[key as keyof typeof newFC]} onChange={e => setNewFC(f => ({ ...f, [key]: e.target.value }))}
                           className="w-full bg-[#F4F2FB] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500" />
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3 px-5 py-4 border-t border-[var(--bd)]">
              <button onClick={() => setModal(false)} className="flex-1 py-2 text-sm text-[var(--tx3)] border border-[var(--bd)] rounded-lg">Annuler</button>
              <button
                onClick={() => createMutation.mutate()}
                disabled={!newFC.bankAccountId || !newFC.amount || !newFC.expectedDate || !newFC.description || createMutation.isPending}
                className="flex-1 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {createMutation.isPending ? 'Création…' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TreasuryDashboard